import {
  ArrowLeft,
  BookOpen,
  Check,
  ClipboardList,
  Lightbulb,
  RefreshCw,
  Save,
  Settings2,
  ShieldCheck,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { api } from '../lib/api';
import type {
  BibleReference,
  EditorialBootstrap,
  EditorialDna,
  EditorialIdea,
  EditorialItem,
  EditorialPlan,
  EditorialSeries,
  Pillar,
} from '../lib/api';
import { Logo } from './Logo';
import { editorialStatusName } from '../lib/editorial-status';

type Tab =
  | 'plan'
  | 'dna'
  | 'pillars'
  | 'series'
  | 'ideas'
  | 'bible'
  | 'rules'
  | 'memory'
  | 'calendar'
  | 'settings';
const tabs: Array<{ id: Tab; label: string }> = [
  { id: 'plan', label: 'Plano do dia' },
  { id: 'dna', label: 'DNA Editorial' },
  { id: 'pillars', label: 'Pilares' },
  { id: 'series', label: 'Séries' },
  { id: 'ideas', label: 'Ideias' },
  { id: 'bible', label: 'Biblioteca bíblica' },
  { id: 'rules', label: 'Regras' },
  { id: 'memory', label: 'Memória' },
  { id: 'calendar', label: 'Calendário' },
  { id: 'settings', label: 'Configuração' },
];
const audiences = [
  'cristãos ativos',
  'cristãos afastados/desanimados',
  'pessoas com dúvidas sobre fé',
  'pessoas enfrentando questões da vida',
];
const categories = [
  'Bíblia',
  'casamento',
  'família',
  'fé',
  'propósito',
  'perguntas',
  'séries',
  'personagens',
  'vida cristã',
  'experiências',
  'tendências',
];

function today() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}
function list(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}
function label(text: string) {
  return text.replaceAll('_', ' ').toLowerCase();
}

function Field({
  title,
  value,
  onChange,
  type = 'text',
  min,
  max,
}: {
  title: string;
  value: string | number;
  onChange: (value: string) => void;
  type?: string;
  min?: number;
  max?: number;
}) {
  return (
    <label className="editorial-field">
      <span>{title}</span>
      <input
        type={type}
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}
function Area({
  title,
  value,
  onChange,
  rows = 3,
}: {
  title: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
}) {
  return (
    <label className="editorial-field">
      <span>{title}</span>
      <textarea
        rows={rows}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

export function EditorialWorkspace({
  session,
  onBack,
  onOpenMedia,
}: {
  session: Session;
  onBack: () => void;
  onOpenMedia: (item: Pick<EditorialItem, 'id' | 'title'>) => void;
}) {
  const [tab, setTab] = useState<Tab>('plan');
  const [data, setData] = useState<EditorialBootstrap | null>(null);
  const [planDate, setPlanDate] = useState(today());
  const [manualAssetId, setManualAssetId] = useState('');
  const [manualTime, setManualTime] = useState('18:00');
  const [day, setDay] = useState<{
    plans: EditorialPlan[];
    items: EditorialItem[];
  } | null>(null);
  const [calendar, setCalendar] = useState<EditorialItem[]>([]);
  const [memory, setMemory] = useState<{
    items: EditorialItem[];
    contents: Array<{ id: string; title: string; status: string }>;
    manualCandidates: Array<{
      id: string;
      content_id: string;
      mime_type: string;
      width: number;
      height: number;
    }>;
    socialMetricsAvailable: boolean;
  } | null>(null);
  const [calendarMode, setCalendarMode] = useState<'day' | 'week' | 'month'>(
    'week',
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await api.editorial.bootstrap(session));
      setError(null);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Falha ao carregar editoria.',
      );
    }
  }, [session]);
  const loadDay = useCallback(async () => {
    try {
      setDay(await api.editorial.day(session, planDate));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Falha ao carregar plano.',
      );
    }
  }, [session, planDate]);
  const loadCalendar = useCallback(async () => {
    const base = new Date(`${planDate}T12:00:00`);
    const start = new Date(base);
    const end = new Date(base);
    if (calendarMode === 'week') {
      start.setDate(start.getDate() - start.getDay());
      end.setDate(start.getDate() + 6);
    }
    if (calendarMode === 'month') {
      start.setDate(1);
      end.setMonth(end.getMonth() + 1, 0);
    }
    const format = (value: Date) =>
      `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`;
    try {
      const result = await api.editorial.calendar(
        session,
        format(start),
        format(end),
      );
      setCalendar(result.items);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : 'Falha ao carregar calendário.',
      );
    }
  }, [session, planDate, calendarMode]);
  const loadMemory = useCallback(async () => {
    try {
      setMemory(await api.editorial.memory(session));
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Falha ao carregar memória.',
      );
    }
  }, [session]);
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    void loadDay();
  }, [loadDay]);
  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);
  useEffect(() => {
    void loadMemory();
  }, [loadMemory]);

  async function act(
    task: () => Promise<unknown>,
    success: string,
    refreshDay = false,
  ) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await task();
      await load();
      await loadMemory();
      if (refreshDay) {
        await loadDay();
        await loadCalendar();
      }
      setNotice(success);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Não foi possível salvar.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="app-shell editorial-shell">
      <header>
        <Logo />
        <button className="editorial-back" onClick={onBack}>
          <ArrowLeft size={16} /> Dashboard
        </button>
      </header>
      <main className="editorial-main">
        <div className="editorial-intro">
          <div>
            <p className="eyebrow">MISSÃO 2 · CÉREBRO EDITORIAL</p>
            <h1>Planejar com fundamento.</h1>
            <p>
              Da ideia ao calendário, com memória, contexto bíblico e decisão
              humana.
            </p>
          </div>
          <span className="editorial-badge">
            <ShieldCheck size={17} /> Sem publicação externa
          </span>
        </div>
        <nav className="editorial-tabs" aria-label="Áreas editoriais">
          {tabs.map((item) => (
            <button
              key={item.id}
              className={tab === item.id ? 'selected' : ''}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </nav>
        {error && (
          <div className="error-banner" role="alert">
            {error}
            <button onClick={() => setError(null)}>Fechar</button>
          </div>
        )}
        {notice && (
          <div className="editorial-notice" role="status">
            <Check size={16} /> {notice}
          </div>
        )}
        {!data ? (
          <div className="editorial-empty">
            <RefreshCw size={24} /> Carregando dados editoriais…
          </div>
        ) : (
          <>
            {tab === 'plan' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">SELEÇÃO RASTREÁVEL</p>
                    <h2>Plano do dia</h2>
                    <p>
                      Até {data.settings[0]?.max_daily_items ?? 3} janelas.
                      Qualidade acima de quantidade.
                    </p>
                  </div>
                  <div className="editorial-actions">
                    <Field
                      title="Data"
                      value={planDate}
                      type="date"
                      onChange={setPlanDate}
                    />
                    <button
                      className="primary compact"
                      disabled={busy || data.automation[0]?.automation_paused}
                      onClick={() =>
                        void act(
                          () => api.editorial.plan(session, planDate),
                          'Plano criado ou recuperado sem duplicações.',
                          true,
                        )
                      }
                    >
                      Planejar dia
                    </button>
                  </div>
                </div>
                {data.automation[0]?.automation_paused && (
                  <p className="editorial-warning">
                    Automações pausadas: o planejador não executará.
                  </p>
                )}
                {data.automation[0]?.operation_mode === 'SUPERVISED' && (
                  <div className="editorial-create">
                    <h3>Revisar manualmente conteúdo existente</h3>
                    <p>
                      Cria apenas uma pauta aguardando sua aprovação. Não agenda
                      publicação nem despausa automações.
                    </p>
                    <div className="editorial-form-grid">
                      <label className="editorial-field">
                        <span>JPEG final validado</span>
                        <select
                          value={manualAssetId}
                          onChange={(event) =>
                            setManualAssetId(event.target.value)
                          }
                        >
                          <option value="">Selecione um conteúdo</option>
                          {memory?.manualCandidates.map((asset) => (
                            <option key={asset.id} value={asset.id}>
                              {memory.contents.find(
                                (content) => content.id === asset.content_id,
                              )?.title ?? 'Conteúdo'}{' '}
                              · {asset.width}×{asset.height}
                            </option>
                          ))}
                        </select>
                      </label>
                      <Field
                        title="Horário editorial (não agenda postagem)"
                        value={manualTime}
                        type="time"
                        onChange={setManualTime}
                      />
                    </div>
                    <button
                      className="secondary"
                      disabled={busy || !manualAssetId}
                      onClick={() => {
                        const asset = memory?.manualCandidates.find(
                          (candidate) => candidate.id === manualAssetId,
                        );
                        if (!asset) return;
                        void act(
                          () =>
                            api.editorial.manualReview(
                              session,
                              asset.content_id,
                              asset.id,
                              planDate,
                              manualTime,
                            ),
                          'Pauta manual criada para revisão. Revise texto, gancho e objetivo antes de aprovar.',
                          true,
                        );
                      }}
                    >
                      Criar pauta para revisão
                    </button>
                  </div>
                )}
                {day?.plans[0] ? (
                  <>
                    <p className="editorial-summary">
                      {day.items.length} pauta(s) · modo{' '}
                      {day.plans[0].mode === 'SUPERVISED'
                        ? 'supervisionado'
                        : 'autônomo'}{' '}
                      · {label(day.plans[0].status)} · correlação{' '}
                      {day.plans[0].correlation_id}
                    </p>
                    <div className="editorial-card-grid">
                      {day.items.map((item) => (
                        <div key={item.id}>
                          <ItemCard
                            item={item}
                            data={data}
                            busy={busy}
                            onSave={(changes) =>
                              act(
                                () =>
                                  api.editorial.updateItem(
                                    session,
                                    item.id,
                                    changes,
                                  ),
                                'Pauta atualizada e auditada.',
                                true,
                              )
                            }
                          />
                          <button
                            className="secondary media-from-editorial"
                            onClick={() =>
                              onOpenMedia({ id: item.id, title: item.title })
                            }
                          >
                            Abrir na Fábrica de Conteúdo →
                          </button>
                        </div>
                      ))}
                    </div>
                    {day.items.length === 0 && (
                      <p className="editorial-empty">
                        Nenhuma ideia atingiu o fundamento ou a qualidade
                        mínimos. Cadastre ou revise ideias e referências antes
                        de tentar outro dia.
                      </p>
                    )}
                  </>
                ) : (
                  <div className="editorial-empty">
                    <ClipboardList size={26} />
                    <strong>Ainda não há plano para {planDate}.</strong>
                    <span>
                      O motor usa ideias, pilares, referências, frequência
                      recente e suas regras de qualidade.
                    </span>
                  </div>
                )}
              </section>
            )}
            {tab === 'dna' && data.dna[0] && (
              <DnaEditor
                key={data.dna[0].version}
                dna={data.dna[0]}
                busy={busy}
                onSave={(changes) =>
                  act(
                    () => api.editorial.saveDna(session, changes),
                    'DNA Editorial salvo com histórico de revisão.',
                  )
                }
              />
            )}
            {tab === 'pillars' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">ORIENTAÇÃO, NÃO QUOTA</p>
                    <h2>Pilares editoriais</h2>
                  </div>
                </div>
                <div className="editorial-card-grid">
                  {data.pillars
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((pillar) => (
                      <PillarEditor
                        key={pillar.id}
                        pillar={pillar}
                        busy={busy}
                        onSave={(changes) =>
                          act(
                            () =>
                              api.editorial.update(
                                session,
                                'pillars',
                                pillar.id,
                                changes,
                              ),
                            'Pilar atualizado.',
                          )
                        }
                      />
                    ))}
                </div>
                <NewPillar
                  busy={busy}
                  onSave={(value) =>
                    act(
                      () => api.editorial.create(session, 'pillars', value),
                      'Novo pilar criado.',
                    )
                  }
                />
              </section>
            )}
            {tab === 'series' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">SEQUÊNCIA EDITORIAL</p>
                    <h2>Séries</h2>
                  </div>
                </div>
                <div className="editorial-card-grid">
                  {data.series
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((series) => (
                      <SeriesEditor
                        key={series.id}
                        series={series}
                        pillars={data.pillars}
                        busy={busy}
                        onSave={(changes) =>
                          act(
                            () =>
                              api.editorial.update(
                                session,
                                'series',
                                series.id,
                                changes,
                              ),
                            'Série atualizada.',
                          )
                        }
                      />
                    ))}
                </div>
                <NewSeries
                  pillars={data.pillars}
                  busy={busy}
                  onSave={(value) =>
                    act(
                      () => api.editorial.create(session, 'series', value),
                      'Nova série criada.',
                    )
                  }
                />
              </section>
            )}
            {tab === 'ideas' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">FONTES INTERNAS E MANUAIS</p>
                    <h2>Banco de ideias</h2>
                    <p>
                      Temas com prioridade, origem, públicos e fundamento
                      rastreáveis.
                    </p>
                  </div>
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() =>
                      void act(
                        () => api.editorial.research(session),
                        'Biblioteca revisada consultada; oportunidades novas foram adicionadas ao banco.',
                      )
                    }
                  >
                    <RefreshCw size={16} /> Pesquisar biblioteca
                  </button>
                </div>
                <IdeaEditor
                  data={data}
                  busy={busy}
                  onSave={(value) =>
                    act(
                      () => api.editorial.create(session, 'ideas', value),
                      'Ideia adicionada ao banco.',
                    )
                  }
                />
                <div className="editorial-card-grid">
                  {data.ideas.map((idea) => (
                    <IdeaRow
                      key={idea.id}
                      idea={idea}
                      data={data}
                      busy={busy}
                      onSave={(changes) =>
                        act(
                          () =>
                            api.editorial.update(
                              session,
                              'ideas',
                              idea.id,
                              changes,
                            ),
                          'Ideia atualizada.',
                        )
                      }
                    />
                  ))}
                </div>
              </section>
            )}
            {tab === 'bible' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">IA NÃO É AUTORIDADE TEOLÓGICA</p>
                    <h2>Biblioteca bíblica</h2>
                    <p>
                      Referência e conteúdo fornecido por você. Texto só com
                      fonte e licença.
                    </p>
                  </div>
                </div>
                <ReferenceEditor
                  busy={busy}
                  onSave={(value) =>
                    act(
                      () => api.editorial.create(session, 'references', value),
                      'Referência criada.',
                    )
                  }
                />
                <div className="editorial-card-grid">
                  {data.references.map((reference) => (
                    <ReferenceCard
                      key={reference.id}
                      reference={reference}
                      busy={busy}
                      onSave={(changes) =>
                        act(
                          () =>
                            api.editorial.update(
                              session,
                              'references',
                              reference.id,
                              changes,
                            ),
                          'Fundamento atualizado.',
                        )
                      }
                    />
                  ))}
                </div>
              </section>
            )}
            {tab === 'rules' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">CRITÉRIOS EDITORIAIS</p>
                    <h2>Regras</h2>
                  </div>
                </div>
                <div className="editorial-card-grid">
                  {data.rules.map((rule) => (
                    <article className="editorial-card" key={rule.id}>
                      <strong>{rule.rule_key.replaceAll('_', ' ')}</strong>
                      <p>{rule.description}</p>
                      <span className="editorial-meta">
                        {rule.severity === 'BLOCK' ? 'Bloqueio' : 'Aviso'}
                      </span>
                      <button
                        className="secondary"
                        disabled={busy}
                        onClick={() =>
                          void act(
                            () =>
                              api.editorial.update(session, 'rules', rule.id, {
                                enabled: !rule.enabled,
                              }),
                            'Regra atualizada.',
                          )
                        }
                      >
                        {rule.enabled
                          ? 'Ativa · desativar'
                          : 'Inativa · ativar'}
                      </button>
                    </article>
                  ))}
                </div>
                <p className="editorial-footnote">
                  Regras críticas de fundamento, repetição e qualidade continuam
                  protegidas no planejador mesmo quando uma preferência visual é
                  alterada.
                </p>
              </section>
            )}
            {tab === 'calendar' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">VISÃO PERSISTIDA</p>
                    <h2>Calendário editorial</h2>
                    <p>Reagende uma pauta no Plano do Dia.</p>
                  </div>
                  <div className="editorial-actions">
                    <Field
                      title="Data base"
                      value={planDate}
                      type="date"
                      onChange={setPlanDate}
                    />
                    <label className="editorial-field">
                      <span>Visão</span>
                      <select
                        value={calendarMode}
                        onChange={(event) =>
                          setCalendarMode(
                            event.target.value as typeof calendarMode,
                          )
                        }
                      >
                        <option value="day">Diária</option>
                        <option value="week">Semanal</option>
                        <option value="month">Mensal</option>
                      </select>
                    </label>
                  </div>
                </div>
                {calendar.length ? (
                  <div className="editorial-card-grid">
                    {calendar.map((item) => (
                      <article className="editorial-card" key={item.id}>
                        <p className="eyebrow">
                          {item.planned_date} · {item.master_time.slice(0, 5)}
                        </p>
                        <h3>{item.title}</h3>
                        <p>
                          {editorialStatusName(item.status)} ·{' '}
                          {label(item.intended_format)}
                        </p>
                        <p className="editorial-meta">
                          {item.selection_reason}
                        </p>
                      </article>
                    ))}
                  </div>
                ) : (
                  <p className="editorial-empty">
                    Nenhuma pauta neste período.
                  </p>
                )}
              </section>
            )}
            {tab === 'memory' && (
              <section className="editorial-section">
                <div className="editorial-heading">
                  <div>
                    <p className="eyebrow">HISTÓRICO REAL · 90 DIAS</p>
                    <h2>Memória editorial</h2>
                    <p>
                      Assuntos usados, decisões de revisão e conteúdos
                      existentes. Sem métricas sociais simuladas.
                    </p>
                  </div>
                </div>
                <div className="editorial-card-grid">
                  <article className="editorial-card">
                    <strong>{memory?.items.length ?? 0} pautas recentes</strong>
                    <p>
                      {memory?.items.filter(
                        (item) => item.status === 'REJECTED',
                      ).length ?? 0}{' '}
                      rejeitadas ·{' '}
                      {memory?.items.filter(
                        (item) =>
                          item.status === 'APPROVED' ||
                          item.status === 'EDITORIALLY_APPROVED',
                      ).length ?? 0}{' '}
                      aprovadas
                    </p>
                    <p>
                      O planejador evita temas usados nos últimos 30 dias e
                      reduz pilares frequentes.
                    </p>
                  </article>
                  <article className="editorial-card">
                    <strong>
                      {memory?.contents.length ?? 0} conteúdos anteriores
                    </strong>
                    <p>
                      Histórico editorial da Fundação, sem inferir desempenho
                      nas plataformas.
                    </p>
                    <p>Métricas sociais: indisponíveis até a Missão 5.</p>
                  </article>
                </div>
                <h3 className="editorial-subheading">Decisões recentes</h3>
                <div className="editorial-card-grid">
                  {memory?.items.slice(0, 20).map((item) => (
                    <article className="editorial-card" key={item.id}>
                      <span className="editorial-meta">
                        {item.planned_date} · {editorialStatusName(item.status)}
                      </span>
                      <h3>{item.title}</h3>
                      <p>{item.selection_reason}</p>
                      {item.review_note && (
                        <p>
                          <strong>Revisão:</strong> {item.review_note}
                        </p>
                      )}
                    </article>
                  ))}
                </div>
                {!memory?.items.length && (
                  <p className="editorial-empty">
                    A memória será preenchida pelos planos e decisões reais.
                  </p>
                )}
              </section>
            )}
            {tab === 'settings' && data.settings[0] && (
              <SettingsEditor
                settings={data.settings[0]}
                mode={data.automation[0]?.operation_mode ?? 'SUPERVISED'}
                busy={busy}
                onSave={(changes) =>
                  act(
                    () => api.editorial.saveSettings(session, changes),
                    'Janelas e qualidade salvas.',
                  )
                }
                onMode={(mode) =>
                  act(
                    () => api.setMode(session, mode),
                    'Modo editorial atualizado.',
                  )
                }
              />
            )}
          </>
        )}
      </main>
      <footer>
        <span>GESTOR DE MÍDIA · MISSÃO 2</span>
        <span>Planejamento apenas · sem postagem</span>
      </footer>
    </div>
  );
}

const dnaTextFields: Array<[keyof EditorialDna, string]> = [
  ['positioning', 'Posicionamento central'],
  ['identity_description', 'Identidade'],
  ['language_style', 'Linguagem'],
  ['tone', 'Tom'],
  ['pastoral_style', 'Estilo pastoral'],
  ['depth', 'Profundidade'],
  ['message_structure', 'Estrutura típica'],
  ['presentation_style', 'Forma de apresentar'],
  ['bible_usage', 'Uso da Bíblia'],
  ['visual_style', 'Estilo visual conceitual'],
];
const dnaListFields: Array<[keyof EditorialDna, string]> = [
  ['preferred_vocabulary', 'Vocabulário preferido'],
  ['avoided_vocabulary', 'Vocabulário a evitar'],
  ['values', 'Valores'],
  ['priority_themes', 'Temas prioritários'],
  ['sensitive_themes', 'Temas sensíveis'],
  ['prohibited_themes', 'Temas evitados'],
  ['hook_types', 'Tipos de gancho'],
  ['ctas', 'CTAs'],
  ['approved_examples', 'Exemplos aprovados'],
  ['unsuitable_examples', 'Exemplos inadequados'],
  ['additional_rules', 'Regras adicionais'],
];
function DnaEditor({
  dna,
  busy,
  onSave,
}: {
  dna: EditorialDna;
  busy: boolean;
  onSave: (changes: Partial<EditorialDna>) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState<EditorialDna>(dna);
  return (
    <section className="editorial-section">
      <div className="editorial-heading">
        <div>
          <p className="eyebrow">VERSÃO {dna.version}</p>
          <h2>DNA Editorial</h2>
          <p>
            O padrão é “Bíblia aplicada à vida real.” A identidade nunca deve
            virar frases genéricas.
          </p>
        </div>
        <button
          className="primary compact"
          disabled={busy}
          onClick={() => void onSave(draft)}
        >
          <Save size={15} /> Salvar DNA
        </button>
      </div>
      <div className="editorial-form-grid">
        {dnaTextFields.map(([key, title]) => (
          <Area
            key={key}
            title={title}
            value={String(draft[key] ?? '')}
            onChange={(value) => setDraft({ ...draft, [key]: value })}
          />
        ))}
        {dnaListFields.map(([key, title]) => (
          <Area
            key={key}
            title={`${title} · separados por vírgula`}
            value={(draft[key] as string[]).join(', ')}
            onChange={(value) => setDraft({ ...draft, [key]: list(value) })}
          />
        ))}
      </div>
    </section>
  );
}

function PillarEditor({
  pillar,
  busy,
  onSave,
}: {
  pillar: Pillar;
  busy: boolean;
  onSave: (changes: Partial<Pillar>) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState(pillar);
  return (
    <article className="editorial-card">
      <Field
        title="Pilar"
        value={draft.name}
        onChange={(name) => setDraft({ ...draft, name })}
      />
      <Area
        title="Descrição"
        value={draft.description}
        onChange={(description) => setDraft({ ...draft, description })}
        rows={2}
      />
      <div className="editorial-inline">
        <Field
          title="Peso de referência %"
          type="number"
          min={0}
          max={100}
          value={draft.weight}
          onChange={(value) => setDraft({ ...draft, weight: Number(value) })}
        />
        <Field
          title="Ordem"
          type="number"
          min={0}
          value={draft.sort_order}
          onChange={(value) =>
            setDraft({ ...draft, sort_order: Number(value) })
          }
        />
      </div>
      <label className="editorial-check">
        <input
          type="checkbox"
          checked={draft.active}
          onChange={(event) =>
            setDraft({ ...draft, active: event.target.checked })
          }
        />{' '}
        Ativo
      </label>
      <button
        className="secondary"
        disabled={busy}
        onClick={() => void onSave(draft)}
      >
        Salvar pilar
      </button>
    </article>
  );
}
function NewPillar({
  busy,
  onSave,
}: {
  busy: boolean;
  onSave: (value: object) => Promise<void> | void;
}) {
  const [name, setName] = useState('');
  return (
    <div className="editorial-add">
      <Field title="Novo pilar" value={name} onChange={setName} />
      <button
        className="secondary"
        disabled={busy || !name.trim()}
        onClick={() => {
          void onSave({
            name,
            description: '',
            weight: 10,
            active: true,
            sort_order: 99,
          });
          setName('');
        }}
      >
        Adicionar pilar
      </button>
    </div>
  );
}

function SeriesEditor({
  series,
  pillars,
  busy,
  onSave,
}: {
  series: EditorialSeries;
  pillars: Pillar[];
  busy: boolean;
  onSave: (changes: Partial<EditorialSeries>) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState(series);
  return (
    <article className="editorial-card">
      <Field
        title="Série"
        value={draft.name}
        onChange={(name) => setDraft({ ...draft, name })}
      />
      <Area
        title="Descrição"
        value={draft.description}
        rows={2}
        onChange={(description) => setDraft({ ...draft, description })}
      />
      <div className="editorial-inline">
        <label className="editorial-field">
          <span>Status</span>
          <select
            value={draft.status}
            onChange={(event) =>
              setDraft({
                ...draft,
                status: event.target.value as EditorialSeries['status'],
              })
            }
          >
            <option value="ACTIVE">Ativa</option>
            <option value="PAUSED">Pausada</option>
            <option value="COMPLETED">Concluída</option>
          </select>
        </label>
        <label className="editorial-field">
          <span>Pilar</span>
          <select
            value={draft.pillar_id ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, pillar_id: event.target.value || null })
            }
          >
            <option value="">Sem pilar</option>
            {pillars.map((pillar) => (
              <option key={pillar.id} value={pillar.id}>
                {pillar.name}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Field
        title="Temas · vírgulas"
        value={draft.themes.join(', ')}
        onChange={(value) => setDraft({ ...draft, themes: list(value) })}
      />
      <Area
        title="Observações"
        value={draft.notes}
        onChange={(notes) => setDraft({ ...draft, notes })}
        rows={2}
      />
      <p className="editorial-meta">
        Planejados {draft.planned_count} · publicados {draft.published_count} ·
        ordem {draft.sort_order}
      </p>
      <button
        className="secondary"
        disabled={busy}
        onClick={() => void onSave(draft)}
      >
        Salvar série
      </button>
    </article>
  );
}
function NewSeries({
  pillars,
  busy,
  onSave,
}: {
  pillars: Pillar[];
  busy: boolean;
  onSave: (value: object) => Promise<void> | void;
}) {
  const [name, setName] = useState('');
  const [pillar, setPillar] = useState('');
  return (
    <div className="editorial-add">
      <Field title="Nova série" value={name} onChange={setName} />
      <label className="editorial-field">
        <span>Pilar</span>
        <select
          value={pillar}
          onChange={(event) => setPillar(event.target.value)}
        >
          <option value="">Sem pilar</option>
          {pillars.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </label>
      <button
        className="secondary"
        disabled={busy || !name.trim()}
        onClick={() => {
          void onSave({
            name,
            description: '',
            status: 'ACTIVE',
            sort_order: 99,
            pillar_id: pillar || null,
            themes: [],
            planned_count: 0,
            published_count: 0,
            start_date: null,
            end_date: null,
            notes: '',
          });
          setName('');
        }}
      >
        Adicionar série
      </button>
    </div>
  );
}

function IdeaEditor({
  data,
  busy,
  onSave,
}: {
  data: EditorialBootstrap;
  busy: boolean;
  onSave: (value: object) => Promise<void> | void;
}) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('Bíblia');
  const [pillarId, setPillarId] = useState('');
  const [seriesId, setSeriesId] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const [priority, setPriority] = useState(70);
  const [selectedAudiences, setSelectedAudiences] = useState<string[]>([
    'cristãos ativos',
  ]);
  const [requires, setRequires] = useState(true);
  return (
    <div className="editorial-create">
      <h3>
        <Lightbulb size={18} /> Nova ideia
      </h3>
      <div className="editorial-form-grid">
        <Field title="Título" value={title} onChange={setTitle} />
        <Area
          title="Descrição / abordagem"
          value={description}
          onChange={setDescription}
          rows={2}
        />
        <label className="editorial-field">
          <span>Categoria</span>
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
          >
            {categories.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label className="editorial-field">
          <span>Pilar</span>
          <select
            value={pillarId}
            onChange={(event) => setPillarId(event.target.value)}
          >
            <option value="">Sem pilar</option>
            {data.pillars.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="editorial-field">
          <span>Série</span>
          <select
            value={seriesId}
            onChange={(event) => setSeriesId(event.target.value)}
          >
            <option value="">Sem série</option>
            {data.series.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="editorial-field">
          <span>Referência bíblica</span>
          <select
            value={referenceId}
            onChange={(event) => setReferenceId(event.target.value)}
          >
            <option value="">Sem referência</option>
            {data.references.map((item) => (
              <option key={item.id} value={item.id}>
                {item.normalized_reference}
              </option>
            ))}
          </select>
        </label>
        <Field
          title="Prioridade · 0 a 100"
          value={priority}
          type="number"
          min={0}
          max={100}
          onChange={(value) => setPriority(Number(value))}
        />
      </div>
      <div className="editorial-audiences">
        <strong>Públicos-alvo</strong>
        {audiences.map((item) => (
          <label className="editorial-check" key={item}>
            <input
              type="checkbox"
              checked={selectedAudiences.includes(item)}
              onChange={(event) =>
                setSelectedAudiences(
                  event.target.checked
                    ? [...selectedAudiences, item]
                    : selectedAudiences.filter((value) => value !== item),
                )
              }
            />{' '}
            {item}
          </label>
        ))}
      </div>
      <label className="editorial-check">
        <input
          type="checkbox"
          checked={requires}
          onChange={(event) => setRequires(event.target.checked)}
        />{' '}
        Exige fundamento bíblico
      </label>
      <button
        className="primary compact"
        disabled={
          busy ||
          title.trim().length < 3 ||
          (requires && !referenceId) ||
          selectedAudiences.length === 0
        }
        onClick={() => {
          const key = title
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, '')
            .slice(0, 160);
          void onSave({
            title,
            description,
            origin: 'MANUAL',
            source_detail: 'Inserção pelo usuário',
            category,
            pillar_id: pillarId || null,
            series_id: seriesId || null,
            audiences: selectedAudiences,
            topic_key: key,
            originating_question: title,
            bible_reference_id: referenceId || null,
            priority,
            potential: 70,
            status: 'AVAILABLE',
            tags: [],
            expires_at: null,
            notes: '',
            related_content_id: null,
            requires_biblical_foundation: requires,
          });
          setTitle('');
          setDescription('');
        }}
      >
        Adicionar ideia
      </button>
    </div>
  );
}
function IdeaRow({
  idea,
  data,
  busy,
  onSave,
}: {
  idea: EditorialIdea;
  data: EditorialBootstrap;
  busy: boolean;
  onSave: (changes: object) => Promise<void> | void;
}) {
  const [draft, setDraft] = useState(idea);
  return (
    <article className="editorial-card">
      <span className="editorial-meta">
        {idea.category} · {idea.origin} · {idea.topic_key}
      </span>
      <Field
        title="Título"
        value={draft.title}
        onChange={(title) => setDraft({ ...draft, title })}
      />
      <Area
        title="Descrição / abordagem"
        value={draft.description}
        onChange={(description) => setDraft({ ...draft, description })}
        rows={2}
      />
      <div className="editorial-inline">
        <label className="editorial-field">
          <span>Status</span>
          <select
            value={draft.status}
            onChange={(event) =>
              setDraft({
                ...draft,
                status: event.target.value as EditorialIdea['status'],
              })
            }
          >
            <option value="AVAILABLE">Disponível</option>
            <option value="PLANNED">Planejada</option>
            <option value="ARCHIVED">Arquivada</option>
          </select>
        </label>
        <Field
          title="Prioridade"
          type="number"
          min={0}
          max={100}
          value={draft.priority}
          onChange={(value) => setDraft({ ...draft, priority: Number(value) })}
        />
        <Field
          title="Potencial"
          type="number"
          min={0}
          max={100}
          value={draft.potential}
          onChange={(value) => setDraft({ ...draft, potential: Number(value) })}
        />
      </div>
      <div className="editorial-inline">
        <label className="editorial-field">
          <span>Pilar</span>
          <select
            value={draft.pillar_id ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, pillar_id: event.target.value || null })
            }
          >
            <option value="">Sem pilar</option>
            {data.pillars.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="editorial-field">
          <span>Série</span>
          <select
            value={draft.series_id ?? ''}
            onChange={(event) =>
              setDraft({ ...draft, series_id: event.target.value || null })
            }
          >
            <option value="">Sem série</option>
            {data.series.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        </label>
        <label className="editorial-field">
          <span>Referência</span>
          <select
            value={draft.bible_reference_id ?? ''}
            onChange={(event) =>
              setDraft({
                ...draft,
                bible_reference_id: event.target.value || null,
              })
            }
          >
            <option value="">Sem referência</option>
            {data.references.map((item) => (
              <option key={item.id} value={item.id}>
                {item.normalized_reference}
              </option>
            ))}
          </select>
        </label>
      </div>
      <Field
        title="Públicos · separados por vírgula"
        value={draft.audiences.join(', ')}
        onChange={(value) => setDraft({ ...draft, audiences: list(value) })}
      />
      <label className="editorial-check">
        <input
          type="checkbox"
          checked={draft.requires_biblical_foundation}
          onChange={(event) =>
            setDraft({
              ...draft,
              requires_biblical_foundation: event.target.checked,
            })
          }
        />{' '}
        Exige fundamento bíblico
      </label>
      <button
        className="secondary"
        disabled={
          busy ||
          (draft.requires_biblical_foundation && !draft.bible_reference_id)
        }
        onClick={() =>
          void onSave({
            title: draft.title,
            description: draft.description,
            status: draft.status,
            priority: draft.priority,
            potential: draft.potential,
            pillar_id: draft.pillar_id,
            series_id: draft.series_id,
            bible_reference_id: draft.bible_reference_id,
            audiences: draft.audiences,
            requires_biblical_foundation: draft.requires_biblical_foundation,
          })
        }
      >
        Salvar ideia
      </button>
    </article>
  );
}

function ReferenceEditor({
  busy,
  onSave,
}: {
  busy: boolean;
  onSave: (value: object) => Promise<void> | void;
}) {
  const [book, setBook] = useState('');
  const [chapter, setChapter] = useState(1);
  const [verse, setVerse] = useState(1);
  return (
    <div className="editorial-create">
      <h3>
        <BookOpen size={18} /> Nova referência
      </h3>
      <div className="editorial-inline">
        <Field title="Livro" value={book} onChange={setBook} />
        <Field
          title="Capítulo"
          type="number"
          min={1}
          value={chapter}
          onChange={(value) => setChapter(Number(value))}
        />
        <Field
          title="Versículo"
          type="number"
          min={1}
          value={verse}
          onChange={(value) => setVerse(Number(value))}
        />
      </div>
      <button
        className="secondary"
        disabled={busy || !book.trim()}
        onClick={() => {
          void onSave({
            book,
            chapter,
            verse_start: verse,
            verse_end: null,
            passage_text: null,
            translation: null,
            source: null,
            text_license: null,
            context: '',
            interpretation: '',
            pastoral_application: '',
            experience_opinion: '',
            notes: '',
            provenance: 'USER_REFERENCE',
            human_reviewed: false,
          });
          setBook('');
        }}
      >
        Adicionar referência sem corpus
      </button>
    </div>
  );
}
function ReferenceCard({
  reference,
  busy,
  onSave,
}: {
  reference: BibleReference;
  busy: boolean;
  onSave: (changes: object) => Promise<void> | void;
}) {
  const [context, setContext] = useState(reference.context);
  const [interpretation, setInterpretation] = useState(
    reference.interpretation,
  );
  const [application, setApplication] = useState(
    reference.pastoral_application,
  );
  const [opinion, setOpinion] = useState(reference.experience_opinion);
  const [reviewed, setReviewed] = useState(reference.human_reviewed);
  const [text, setText] = useState(reference.passage_text ?? '');
  const [source, setSource] = useState(reference.source ?? '');
  const [license, setLicense] = useState(reference.text_license ?? '');
  const [translation, setTranslation] = useState(reference.translation ?? '');
  return (
    <article className="editorial-card">
      <span className="editorial-meta">{reference.provenance}</span>
      <h3>{reference.normalized_reference}</h3>
      <p className="editorial-meta">
        {reference.passage_text
          ? `${reference.translation ?? 'Texto'} · ${reference.text_license ?? 'Licença não informada'}`
          : 'Sem texto bíblico incorporado'}
      </p>
      <Area
        title="Texto bíblico · somente se fornecido com licença"
        value={text}
        onChange={setText}
        rows={3}
      />
      <div className="editorial-inline">
        <Field title="Tradução" value={translation} onChange={setTranslation} />
        <Field title="Fonte" value={source} onChange={setSource} />
        <Field
          title="Licença / permissão"
          value={license}
          onChange={setLicense}
        />
      </div>
      <Area title="Contexto" value={context} onChange={setContext} rows={2} />
      <Area
        title="Interpretação"
        value={interpretation}
        onChange={setInterpretation}
        rows={2}
      />
      <Area
        title="Aplicação pastoral"
        value={application}
        onChange={setApplication}
        rows={2}
      />
      <Area
        title="Experiência / opinião (separada da Escritura)"
        value={opinion}
        onChange={setOpinion}
        rows={2}
      />
      <label className="editorial-check">
        <input
          type="checkbox"
          checked={reviewed}
          onChange={(event) => setReviewed(event.target.checked)}
        />{' '}
        Revisão humana concluída
      </label>
      <button
        className="secondary"
        disabled={
          busy ||
          (reviewed &&
            (!context.trim() ||
              !interpretation.trim() ||
              !application.trim())) ||
          (!!text.trim() && (!source.trim() || !license.trim()))
        }
        onClick={() =>
          void onSave({
            passage_text: text.trim() || null,
            translation: translation.trim() || null,
            source: source.trim() || null,
            text_license: license.trim() || null,
            context,
            interpretation,
            pastoral_application: application,
            experience_opinion: opinion,
            human_reviewed: reviewed,
          })
        }
      >
        Salvar fundamento
      </button>
    </article>
  );
}

function ItemCard({
  item,
  data,
  busy,
  onSave,
}: {
  item: EditorialItem;
  data: EditorialBootstrap;
  busy: boolean;
  onSave: (changes: object) => Promise<void> | void;
}) {
  const [script, setScript] = useState(item.script);
  const [title, setTitle] = useState(item.title);
  const [approach, setApproach] = useState(item.approach);
  const [hook, setHook] = useState(item.hook);
  const [objective, setObjective] = useState(item.objective);
  const [cta, setCta] = useState(item.cta);
  const [biblicalTextNote, setBiblicalTextNote] = useState(
    item.biblical_text_note,
  );
  const [contextNote, setContextNote] = useState(item.context_note);
  const [interpretationNote, setInterpretationNote] = useState(
    item.interpretation_note,
  );
  const [applicationNote, setApplicationNote] = useState(item.application_note);
  const [opinionNote, setOpinionNote] = useState(item.opinion_note);
  const [reviewNote, setReviewNote] = useState(item.review_note);
  const [plannedDate, setPlannedDate] = useState(item.planned_date);
  const [masterTime, setMasterTime] = useState(item.master_time.slice(0, 5));
  const [format, setFormat] = useState(item.intended_format);
  const [platforms, setPlatforms] = useState(item.intended_platforms);
  const manualReview = item.source_provenance === 'MANUAL_EXISTING_CONTENT';
  const manualReady =
    !manualReview ||
    [reviewNote, hook, objective, script, cta].every(
      (value) => value.trim() && !value.trim().startsWith('[Revisar'),
    );
  const edits = {
    title,
    approach,
    hook,
    objective,
    cta,
    script,
    biblical_text_note: biblicalTextNote,
    context_note: contextNote,
    interpretation_note: interpretationNote,
    application_note: applicationNote,
    opinion_note: opinionNote,
    review_note: reviewNote,
    planned_date: plannedDate,
    master_time: masterTime,
    intended_format: format,
    intended_platforms: platforms,
  };
  return (
    <article className="editorial-card editorial-item">
      <p className="eyebrow">
        JANELA {item.slot} · {item.master_time.slice(0, 5)} ·{' '}
        {editorialStatusName(item.status)}
      </p>
      <h3>{item.title}</h3>
      {manualReview && item.status !== 'APPROVED' && (
        <p className="editorial-warning">
          Esta pauta veio de um conteúdo existente. Revise gancho, objetivo,
          roteiro e CTA; registre uma nota de revisão antes de aprovar.
        </p>
      )}
      {item.status === 'APPROVED' && (
        <p>
          Pauta aprovada para produzir mídia. Publicar exige um final validado.
        </p>
      )}
      <p>
        <strong>Gancho:</strong> {item.hook}
      </p>
      <p>
        <strong>Objetivo:</strong> {item.objective}
      </p>
      <p>
        <strong>Pilar:</strong>{' '}
        {data.pillars.find((pillar) => pillar.id === item.pillar_id)?.name ??
          'Sem pilar'}{' '}
        · <strong>Série:</strong>{' '}
        {data.series.find((series) => series.id === item.series_id)?.name ??
          'Livre'}
      </p>
      <p>
        <strong>Referência:</strong> {item.biblical_text_note}
      </p>
      <details>
        <summary>Fundamento e seleção</summary>
        <p>
          <strong>Contexto:</strong> {item.context_note || 'Revisar'}
        </p>
        <p>
          <strong>Interpretação:</strong>{' '}
          {item.interpretation_note || 'Revisar'}
        </p>
        <p>
          <strong>Aplicação:</strong> {item.application_note || 'Desenvolver'}
        </p>
        <p>
          <strong>Opinião:</strong>{' '}
          {item.opinion_note || 'Separar da Escritura'}
        </p>
        <p>{item.selection_reason}</p>
        <p>Origem: {item.source_provenance}</p>
      </details>
      <div className="editorial-form-grid">
        <Field title="Título da pauta" value={title} onChange={setTitle} />
        <Area
          title="Abordagem"
          value={approach}
          onChange={setApproach}
          rows={2}
        />
        <Field title="Gancho" value={hook} onChange={setHook} />
        <Field title="Objetivo" value={objective} onChange={setObjective} />
        <Field title="CTA" value={cta} onChange={setCta} />
      </div>
      <details>
        <summary>Editar fundamento · separar cada camada</summary>
        <Area
          title="Texto / referência"
          value={biblicalTextNote}
          onChange={setBiblicalTextNote}
          rows={2}
        />
        <Area
          title="Contexto"
          value={contextNote}
          onChange={setContextNote}
          rows={2}
        />
        <Area
          title="Interpretação"
          value={interpretationNote}
          onChange={setInterpretationNote}
          rows={2}
        />
        <Area
          title="Aplicação pastoral"
          value={applicationNote}
          onChange={setApplicationNote}
          rows={2}
        />
        <Area
          title="Experiência / opinião"
          value={opinionNote}
          onChange={setOpinionNote}
          rows={2}
        />
      </details>
      <Area
        title="Roteiro estruturado"
        value={script}
        onChange={setScript}
        rows={8}
      />
      <Area
        title="Motivo / pedido de ajuste"
        value={reviewNote}
        onChange={setReviewNote}
        rows={2}
      />
      <div className="editorial-inline">
        <Field
          title="Reagendar para"
          type="date"
          value={plannedDate}
          onChange={setPlannedDate}
        />
        <Field
          title="Horário mestre"
          type="time"
          value={masterTime}
          onChange={setMasterTime}
        />
        <label className="editorial-field">
          <span>Formato pretendido</span>
          <select
            value={format}
            onChange={(event) => setFormat(event.target.value)}
          >
            {['IMAGE', 'CAROUSEL', 'REEL', 'SHORT_VIDEO', 'STORY', 'TEXT'].map(
              (value) => (
                <option value={value} key={value}>
                  {label(value)}
                </option>
              ),
            )}
          </select>
        </label>
      </div>
      <div className="editorial-audiences">
        <strong>Plataformas pretendidas · apenas intenção futura</strong>
        {(['instagram', 'facebook', 'tiktok'] as const).map((platform) => (
          <label className="editorial-check" key={platform}>
            <input
              type="checkbox"
              checked={platforms.includes(platform)}
              onChange={(event) =>
                setPlatforms(
                  event.target.checked
                    ? [...platforms, platform]
                    : platforms.filter((value) => value !== platform),
                )
              }
            />{' '}
            {platform}
          </label>
        ))}
      </div>
      <div className="editorial-actions">
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void onSave(edits)}
        >
          Salvar ajustes
        </button>
        <button
          className="secondary"
          disabled={busy}
          onClick={() =>
            void onSave({
              status: 'ADJUSTMENT_REQUESTED',
              review_note: reviewNote,
              script,
            })
          }
        >
          Solicitar ajuste
        </button>
        <button
          className="secondary"
          disabled={busy}
          onClick={() =>
            void onSave({ status: 'REJECTED', review_note: reviewNote })
          }
        >
          Rejeitar
        </button>
        <button
          className="primary compact"
          disabled={busy || !manualReady}
          onClick={() => void onSave({ ...edits, status: 'APPROVED' })}
        >
          <Check size={15} /> Aprovar
        </button>
      </div>
    </article>
  );
}

function SettingsEditor({
  settings,
  mode,
  busy,
  onSave,
  onMode,
}: {
  settings: EditorialBootstrap['settings'][number];
  mode: 'SUPERVISED' | 'AUTONOMOUS';
  busy: boolean;
  onSave: (changes: object) => Promise<void> | void;
  onMode: (mode: 'SUPERVISED' | 'AUTONOMOUS') => Promise<void> | void;
}) {
  const [max, setMax] = useState(settings.max_daily_items);
  const [windows, setWindows] = useState(settings.windows.join(', '));
  const [quality, setQuality] = useState(settings.min_quality);
  return (
    <section className="editorial-section">
      <div className="editorial-heading">
        <div>
          <p className="eyebrow">JANELAS E GOVERNANÇA</p>
          <h2>Configuração editorial</h2>
        </div>
        <Settings2 size={22} />
      </div>
      <div className="editorial-create">
        <div className="editorial-form-grid">
          <Field
            title="Máximo diário (1–3)"
            type="number"
            min={1}
            max={3}
            value={max}
            onChange={(value) => setMax(Number(value))}
          />
          <Field
            title="Qualidade mínima (0–100)"
            type="number"
            min={0}
            max={100}
            value={quality}
            onChange={(value) => setQuality(Number(value))}
          />
          <Field
            title="Janelas · HH:MM separadas por vírgula"
            value={windows}
            onChange={setWindows}
          />
        </div>
        <button
          className="primary compact"
          disabled={busy}
          onClick={() =>
            void onSave({
              max_daily_items: max,
              min_quality: quality,
              windows: list(windows),
            })
          }
        >
          <Save size={15} /> Salvar janelas
        </button>
      </div>
      <div className="editorial-create">
        <h3>Modo de operação</h3>
        <p>
          Supervisionado exige aprovação de cada pauta. Autônomo só aprova
          editorialmente pautas cujo fundamento bíblico já foi revisado; nunca
          publica nas redes.
        </p>
        <div className="editorial-actions">
          <button
            className={mode === 'SUPERVISED' ? 'primary compact' : 'secondary'}
            disabled={busy}
            onClick={() => void onMode('SUPERVISED')}
          >
            Supervisionado
          </button>
          <button
            className={mode === 'AUTONOMOUS' ? 'primary compact' : 'secondary'}
            disabled={busy}
            onClick={() => void onMode('AUTONOMOUS')}
          >
            Autônomo
          </button>
        </div>
      </div>
    </section>
  );
}
